import type {
  ModalIbanAccountStep,
  QuizResponses,
  QuizStepProps
} from "@/features/user/quiz/types.ts";
import { useTranslation } from "react-i18next";
import { Base, SectionTitleSmall } from "@/atoms-react/text/Text.react.tsx";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react.tsx";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react.tsx";
import { Button } from "@/atoms-react/button/Button.react.tsx";
import { Themes } from "@/types/theme.ts";
import { useQuiz } from "@/features/user/quiz/hooks/use-quiz.ts";
import Loader from "@/atoms-react/loader/Loader.react.tsx";
import { usei18n } from "@/features/utils/hooks/use-i18n";

export const AccountReasonStep = ({
  setStep,
  quizResponses,
  setQuizResponses,
  stepSettings,
  setPrevStep,
  totalSteps = 8
}: QuizStepProps) => {
  const { t } = useTranslation(["pass"]);
  const { getLanguage } = usei18n();

  const { AccountReasonsQuery } = useQuiz();
  const { data: account_reasons, isLoading: isLoadingAccountReasons } = AccountReasonsQuery(
    getLanguage()
  );
  const handleCheckboxChange = (id: string, isChecked: boolean) =>
    setQuizResponses?.((prev) => {
      const newElement = isChecked
        ? [...prev.account_reasons, { id }]
        : prev.account_reasons.filter((income) => Number(income.id) !== Number(id));

      return {
        ...(prev as QuizResponses),
        account_reasons: newElement
      };
    });

  const isQuizAnswered = () => {
    if (!quizResponses?.account_reasons?.length) return false;
    return true;
  };

  return (
    <>
      <SectionTitleSmall>
        {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.account_reasons.question")}
      </SectionTitleSmall>

      {isLoadingAccountReasons && (
        <div className="mt-4 mb-4 flex flex-1 items-center justify-center">
          <Loader className="text-gray-500" />
        </div>
      )}
      {!isLoadingAccountReasons && (
        <>
          <div className="my-4 mt-4 w-full space-y-4">
            {account_reasons &&
              account_reasons.map((c,index) => (
                <Checkbox
                  key={c.id}
                  dataCy={`account-reason-${index}`}
                  value={
                    quizResponses?.account_reasons &&
                    quizResponses.account_reasons.some(
                      (income) => Number(income.id) === Number(c.id)
                    )
                  }
                  onChange={(value) => handleCheckboxChange(c.id, value)}
                >
                  <Base className="mb-1 cursor-pointer">
                    {getLanguage() === "fr" ? c.name : c.english_name}
                  </Base>
                </Checkbox>
              ))}
          </div>
          <div className="mt-auto mb-4 flex flex-col items-center">
            <DotsNavigation total={totalSteps} current={stepSettings.current} className="mb-4" />
            <div className="flex w-full flex-col-reverse gap-y-2 md:flex-row md:gap-x-2">
              <Button
                theme={Themes.white}
                onClick={() => setPrevStep(stepSettings.previous as ModalIbanAccountStep)}
              >
                {t("olkypass.general.go_back")}
              </Button>

              <Button
                disabled={!isQuizAnswered()}
                onClick={() => setStep(stepSettings.next as ModalIbanAccountStep)}
                dataCy="account-reason-step-continue"
              >
                {t("olkypass.general.continue")}
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
